import { useCallback, useEffect } from 'react';
import { useI18n } from 'next-localization';

import { cn } from '@/lib/utils';
import { NeuSelect } from '@hcahealthcaredpx/neutron-react';

import { DEFAULT_SORT_BY, DISTANCE_SORT_BY, ITEMS_PER_PAGE } from '../../constants';
import { useListingContext } from '../../hooks';
import styles from '../../listing.module.css';

export const SortByFilter = ({
  hasDistance,
  totalProvidersCount,
}: {
  hasDistance: boolean;
  totalProvidersCount: number;
}): JSX.Element => {
  const { t } = useI18n();

  const { appliedFilters, setAppliedFilters } = useListingContext();

  const sortBy = appliedFilters?.sortType || DEFAULT_SORT_BY;

  const shouldUseDistance = hasDistance && !appliedFilters.sortType;

  const list = [
    { label: t('OAS.Listing.SelectBelow'), value: DEFAULT_SORT_BY, selected: !hasDistance },
    { label: 'A-Z', value: 'Ascending' },
    { label: 'Z-A', value: 'Descending' },
    ...(hasDistance
      ? [{ label: t('OAS.Listing.Distance'), value: DISTANCE_SORT_BY, selected: true }]
      : []),
  ];

  const updateSortFilter = useCallback(
    (sortType: string | null) => {
      setAppliedFilters((prev) => ({
        ...prev,
        sortType,
        meta: {
          page: 1,
          pageSize: ITEMS_PER_PAGE,
        },
      }));
    },
    [setAppliedFilters]
  );

  useEffect(() => {
    if (shouldUseDistance) {
      updateSortFilter(DISTANCE_SORT_BY);
    }
  }, [shouldUseDistance, updateSortFilter]);

  const handleSortBy = (value: string) => {
    updateSortFilter(value === DEFAULT_SORT_BY ? null : value);
  };

  return (
    <div
      id="sort-by-menu"
      aria-label={t('Search.Sort')}
      className={cn('col-md-6 col-lg-3 d-flex justify-content-end', styles.sortByMenu)}
    >
      {totalProvidersCount > 0 && (
        <NeuSelect
          label={t('OAS.Listing.Sort')}
          aria-labelledby="sort-by-menu"
          value={list?.find((item) => item.value === sortBy)}
          onNeuChange={(e) => handleSortBy(e.value)}
          items={list}
          className="w-100"
          required
        />
      )}
    </div>
  );
};
